import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'lodash';
import history from 'app/routes/history';
import {
  IMatchPaymentsSelector,
  IBulkMatchPaymentsSelector,
  ITeamsSelector,
  IUsersSelector,
  IPurchasesSelector,
  IForfeitsSelector,
} from 'app/types/selector';

import * as matchPaymentsActions from 'app/ducks/matchPayments/actions';
import * as bulkMatchPaymentsActions from 'app/ducks/bulkMatchPayments/actions';
import * as forfeitsActions from 'app/ducks/forfeits/actions';
import * as purchasesActions from 'app/ducks/purchases/actions';
import * as creditCardActions from 'app/ducks/creditCards/actions';
import cartActions from 'app/ducks/cart/actions';

import * as loadingSelectors from 'app/ducks/loading/selectors';
import * as matchPaymentsSelectors from 'app/ducks/matchPayments/selectors';
import * as bulkMatchPaymentsSelectors from 'app/ducks/bulkMatchPayments/selectors';
import * as forfeitsSelectors from 'app/ducks/forfeits/selectors';
import * as purchasesSelectors from 'app/ducks/purchases/selectors';
import * as teamsSelectors from 'app/ducks/teams/selectors';
import * as creditCardSelectors from 'app/ducks/creditCards/selectors';
import * as usersSelectors from 'app/ducks/users/selectors';
import * as cartSelectors from 'app/ducks/cart/selectors';

import CheckoutTeamManagerOrgPayment from './CheckoutTeamManagerOrgPayment';
import CheckoutTeamManagerOrgVariablePayment from './CheckoutTeamManagerOrgVariablePayment';
import CheckoutTeamManagerOrgVariablePaymentAddition from './CheckoutTeamManagerOrgVariablePaymentAddition';
import CheckoutForfeitPayment from './CheckoutForfeitPayment';
import CheckoutBulkMatchPayment from './CheckoutBulkMatchPayment';

function selector(state): any {
  return {
    appReady: loadingSelectors.appReady(state),
    matchPayments: matchPaymentsSelectors.getMatchPayments(
      state,
    ),
    bulkMatchPayments: bulkMatchPaymentsSelectors.getBulkMatchPayments(
      state,
    ),
    forfeits: forfeitsSelectors.getForfeits(
      state,
    ),
    creditCards: creditCardSelectors.getCreditCards(state),
    teams: teamsSelectors.getTeams(state),
    users: usersSelectors.getUsers(state),
    cart: cartSelectors.getCart(state),
    purchases: purchasesSelectors.getPurchases(state),
    errors: {
      ...state.purchases.backendErrors,
      ...state.creditCards.backendErrors,
    },
    processingBackendErrors: state.purchases.processingBackendErrors,
  };
}

const actionCreators = _.merge(
  forfeitsActions,
  bulkMatchPaymentsActions,
  matchPaymentsActions,
  creditCardActions,
  purchasesActions,
  cartActions,
);

interface ICheckoutProps {
  matchPayments: IMatchPaymentsSelector;
  bulkMatchPayments: IBulkMatchPaymentsSelector;
  creditCards: any;
  teams: ITeamsSelector;
  users: IUsersSelector;
  purchases: IPurchasesSelector;
  forfeits: IForfeitsSelector;
  match: any;
  actions: any;
}

interface ICheckoutState {
  timerCount: number;
}

@(connect(selector, (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
})) as any)
export default class Checkout extends React.Component<ICheckoutProps, ICheckoutState> {
  timer;

  static defaultProps: ICheckoutProps = {
    purchases: {},
    matchPayments: {},
    bulkMatchPayments: {},
    forfeits: {},
    teams: {},
    creditCards: {},
    users: {},
    match: undefined,
    actions: undefined,
  };

  state = {
    timerCount: 0,
  }

  componentDidMount = () => {
    if (this.props.match.params.matchPaymentId && !this.props.matchPayments.hasLoaded) {
      this.props.actions.getMatchPayment(
        this.props.match.params.matchPaymentId,
      );
    } else if (this.props.match.params.forfeitId && !this.props.forfeits.hasLoaded) {
      this.props.actions.getForfeit(
        this.props.match.params.forfeitId,
      );
    } else if (this.props.match.params.bulkMatchPaymentId && !this.props.bulkMatchPayments.hasLoaded) {
      this.props.actions.getBulkMatchPayment(
        this.props.match.params.bulkMatchPaymentId,
      );
    }
    this.props.actions.clearPurchase();
    this.timer = setInterval(this.checkForPurchaseUpdates, 5000);
  };

  componentDidUpdate = () => {
    const { currentMatchPayment } = this.props.matchPayments;
    const { currentForfeit } = this.props.forfeits;

    if (this.props.match.params.matchPaymentId && _.includes(['collected'], currentMatchPayment.status)) {
      history.push(`/payment/${currentMatchPayment.id}/receipt`);
    }
    if (this.props.match.params.forfeitId && _.includes(['confirmed'], currentForfeit.status)) {
      history.push(`/payment/${currentForfeit.id}/receipt`);
    }
  };

  componentWillUnmount(): void {
    clearInterval(this.timer);
    this.timer = null;
  }

  checkForPurchaseUpdates = (): void => {
    if (this.props.purchases?.currentPurchase?.status === 'processing-async' && this.state.timerCount < 20) {
      this.setState((previousState) => ({ timerCount: previousState.timerCount + 1 }));
      this.props.actions.getPurchase(this.props.purchases?.currentPurchase?.id);
    } else if (this.props.purchases?.currentPurchase?.status === 'successful') {
      clearInterval(this.timer);
    }
  }

  render(): React.ReactNode {
    const { currentBulkMatchPayment } = this.props.bulkMatchPayments;
    const { currentMatchPayment } = this.props.matchPayments;
    const { currentForfeit } = this.props.forfeits;
    let checkoutType = _.includes(this.props.match.path, 'forfeit') ? 'forfeit' : 'match';
    if (_.includes(this.props.match.path, 'multiple-matches')) {
      checkoutType = 'multiple-matches';
    }
    if (checkoutType === 'forfeit') {
      if (!_.isEmpty(currentForfeit)) {
        return (
          <CheckoutForfeitPayment
            key={`checkout-forfeit-${currentMatchPayment.paymentCategory}-${this.props.match.params.matchPaymentId}`}
            {...this.props}
            isLoading={
              this.props.purchases.isSaving
            }
            cardIsLoading={
              this.props.creditCards.isLoading
            }
          />
        );
      }
    }
    if (checkoutType === 'multiple-matches') {
      if (!_.isEmpty(currentBulkMatchPayment)) {
        return (
          <CheckoutBulkMatchPayment
            key={`checkout-org-${this.props.match.params.bulkMatchPaymentId}`}
            {...this.props}
            isLoading={
              this.props.purchases.isSaving
            }
            cardIsLoading={
              this.props.creditCards.isLoading
            }
          />
        );
      }
    }
    if (checkoutType === 'match' && currentMatchPayment.paymentType === 'event') {
      if (!_.isEmpty(currentMatchPayment)) {
        return (
          <CheckoutTeamManagerOrgPayment
            key={`checkout-org-${currentMatchPayment.paymentCategory}-${this.props.match.params.matchPaymentId}`}
            {...this.props}
            isLoading={
              this.props.purchases.isSaving
            }
            cardIsLoading={
              this.props.creditCards.isLoading
            }
          />
        );
      }
    }
    if (checkoutType && currentMatchPayment.paymentType === 'event-variable') {
      if (!_.isEmpty(currentMatchPayment)) {
        return (
          <CheckoutTeamManagerOrgVariablePayment
            key={`checkout-org-variable-${currentMatchPayment.paymentCategory}-${this.props.match.params.matchPaymentId}`}
            {...this.props}
            isLoading={
              this.props.purchases.isSaving
            }
            cardIsLoading={
              this.props.creditCards.isLoading
            }
          />
        );
      }
    }
    if (checkoutType && currentMatchPayment.paymentType === 'event-variable-addition') {
      if (!_.isEmpty(currentMatchPayment)) {
        return (
          <CheckoutTeamManagerOrgVariablePaymentAddition
            key={`checkout-org-variable-addition-${currentMatchPayment.paymentCategory}-${this.props.match.params.matchPaymentId}`}
            {...this.props}
            isLoading={
              this.props.purchases.isSaving
            }
            cardIsLoading={
              this.props.creditCards.isLoading
            }
          />
        );
      }
    }
    return null;
  }
}
